import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import { Divider, Icon, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { MuiTelInput } from 'mui-tel-input';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';

import AddressAutocomplete from '@/components/address-autocomplete';
import Button from '@/components/Button';
import InputField from '@/components/InputField';
import useResponsive from '@/hooks/useResponsive';
import {
  ALLOWED_COUNTRIES,
  CountryCode,
  DEFAULT_COUNTRY,
  MAX_PHONE_LENGTH_BY_COUNTRY,
  SendToClientFormData,
} from '@/sections/Fulfill/Inventory/dependencies/inventorySchema';
import { getZipcodeConfig, getZipcodeInputProps } from '@/utils/zipcodeValidation';

import type { AddressDetails } from '@/components/address-autocomplete';

interface StepDestinationProps {
  step: number;
  setCurrentStep: (step: number) => void;
  register: UseFormRegister<SendToClientFormData>;
  errors: FieldErrors<SendToClientFormData>;
  control: Control<SendToClientFormData>;
  watch: UseFormWatch<SendToClientFormData>;
  handleChangeSelectedCountryPhone: (country: CountryCode) => void;
  onAddressChange: (details: AddressDetails) => void;
  onAddressManualChange: () => void;
  onAddressBlur: () => void;
  editStep: boolean;
  isDisabled: boolean;
  isAddressConfirmed: boolean;
  isAddressTouched: boolean;
  defaultCountryPhone: CountryCode;
}

const StepDestination = ({
  step,
  setCurrentStep,
  register,
  errors,
  control,
  watch,
  handleChangeSelectedCountryPhone,
  onAddressChange,
  onAddressManualChange,
  onAddressBlur,
  editStep,
  isDisabled,
  isAddressConfirmed,
  isAddressTouched,
  defaultCountryPhone,
}: StepDestinationProps) => {
  const theme = useTheme();
  const isSmallDrawer = useResponsive('between', 'lg', 'xl');
  const isPhone = useResponsive('down', 'sm');

  // Get the selected country and its configuration
  const selectedCountry = watch('destination.country') || 'MEX';
  const zipcodeConfig = getZipcodeConfig(selectedCountry);
  const zipcodeInputProps = getZipcodeInputProps(selectedCountry);
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          margin: '1rem 0',
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ minWidth: '4.4rem' }}>
          Paso <span style={{ color: theme.palette.primary.main }}>2</span>
        </Typography>
        <Divider
          orientation={isSmallDrawer || isPhone ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            margin: isSmallDrawer || isPhone ? '0' : `0 ${theme.spacing(2)}`,
          }}
        />
        <Box>
          <Typography variant="subtitle2">¿A quién deseas enviar tus productos?</Typography>
        </Box>
      </Box>

      <Grid
        container
        direction="row"
        spacing={{ xxs: 0, md: 2, lg: 0, xl: 2 }}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Grid size={{ xxs: 12, md: 6, lg: 12, xl: 6 }}>
          <InputField
            label={
              <>
                Nombre <span style={{ color: theme.palette.error.main }}>*</span>
              </>
            }
            placeholder="Nombre"
            name="recipient.firstName"
            errors={errors}
            register={register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<PersonOutlineOutlinedIcon />}
          />
        </Grid>
        <Grid size={{ xxs: 12, md: 6, lg: 12, xl: 6 }}>
          <InputField
            label={
              <>
                Apellido <span style={{ color: theme.palette.error.main }}>*</span>
              </>
            }
            placeholder="Apellido"
            name="recipient.lastName"
            errors={errors}
            register={register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<PersonOutlineOutlinedIcon />}
          />
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        spacing={{ xxs: 0, md: 2, lg: 0, xl: 2 }}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Grid size={{ xxs: 12, md: 6, lg: 12, xl: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0rem' }}>
            <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
              <PhoneIphoneOutlinedIcon />
            </Icon>

            <Typography
              variant="body1"
              sx={{
                color: theme.palette.secondary.main,
                fontSize: '1rem',
                fontWeight: 'bold',
                margin: '1rem 0.5rem 1rem 0',
              }}
            >
              Teléfono <span style={{ color: theme.palette.error.main }}>*</span>
            </Typography>
          </Box>
          <Controller
            name="recipient.phone"
            control={control}
            render={({ field }) => (
              <MuiTelInput
                sx={{ width: '100%' }}
                onlyCountries={ALLOWED_COUNTRIES}
                defaultCountry={defaultCountryPhone}
                error={!!errors.recipient?.phone}
                helperText={errors.recipient?.phone?.message}
                {...field}
                onChange={(value, country) => {
                  const newCountry = (country.countryCode as CountryCode) || DEFAULT_COUNTRY;
                  const maxLength =
                    MAX_PHONE_LENGTH_BY_COUNTRY[newCountry] ||
                    MAX_PHONE_LENGTH_BY_COUNTRY[DEFAULT_COUNTRY];
                  const cleanValue = value.replace(/\s/g, '');
                  const finalValue =
                    cleanValue.length <= maxLength
                      ? cleanValue
                      : cleanValue.substring(0, maxLength);
                  field.onChange(finalValue);
                  handleChangeSelectedCountryPhone(newCountry);
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xxs: 12, md: 6, lg: 12, xl: 6 }}>
          <InputField
            label={
              <>
                Email <span style={{ color: theme.palette.error.main }}>*</span>
              </>
            }
            placeholder="Email"
            name="recipient.email"
            errors={errors}
            register={register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<EmailOutlinedIcon />}
          />
        </Grid>
      </Grid>
      <Grid size={{ xxs: 12 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
            <FmdGoodOutlinedIcon />
          </Icon>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              margin: '1rem 0.5rem 1rem 0',
              color: theme.palette.secondary.main,
              fontSize: '1rem',
            }}
          >
            Dirección <span style={{ color: theme.palette.error.main }}>*</span>
          </Typography>
        </Box>
        <Controller
          name="destination.address"
          control={control}
          render={({ field }) => (
            <AddressAutocomplete
              size="medium"
              error={Boolean(isAddressTouched && !isAddressConfirmed)}
              value={field.value}
              country={watch('destination.country') || defaultCountryPhone || 'MEX'}
              placeholder="Calle y número"
              onSelectAddress={(details) => {
                field.onChange(details.address);
                onAddressChange(details);
              }}
              onChange={(value: string) => {
                field.onChange(value);
                onAddressManualChange();
              }}
              onBlur={onAddressBlur}
            />
          )}
        />
        {errors?.destination?.address && (
          <Typography variant="body2" color={theme.palette.error.main}>
            Este campo es requerido
          </Typography>
        )}
        {errors?.destination?.country && (
          <Typography variant="body2" color={theme.palette.error.main}>
            {errors.destination.country.message}
          </Typography>
        )}
      </Grid>
      <Grid
        container
        direction="row"
        spacing={{ xxs: 0, md: 2, lg: 0, xl: 2 }}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Grid size={{ xxs: 12, md: 6, lg: 12, xl: 6 }}>
          <InputField
            label="Referencias"
            name="destination.reference"
            errors={errors}
            register={register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<ManageSearchIcon />}
          />
        </Grid>
        <Grid size={{ xxs: 12, md: 6, lg: 12, xl: 6 }}>
          <InputField
            label={
              <>
                {zipcodeConfig.label}

                {zipcodeConfig.required && (
                  <span style={{ color: theme.palette.error.main }}> *</span>
                )}
              </>
            }
            placeholder={zipcodeConfig.label}
            name="destination.zipcode"
            errors={errors}
            register={register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<FmdGoodIcon />}
            textFieldProps={zipcodeInputProps}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column-reverse', sm: 'row', lg: 'column-reverse', xl: 'row' },
          justifyContent: { xxs: 'center', sm: 'space-between', lg: 'center', xl: 'space-between' },
          width: '100%',
          alignItems: 'center',
          gap: '1rem',
        }}
        mt={4}
      >
        <Button variant="outlined" onClick={() => setCurrentStep(step - 1)}>
          Atras
        </Button>
        <Button
          disabled={isDisabled}
          onClick={() => (editStep ? setCurrentStep(4) : setCurrentStep(step + 1))}
        >
          Continuar
        </Button>
      </Box>
    </Box>
  );
};

export default StepDestination;
